
import { Badge } from "@/components/ui/badge";
import { SparklesIcon, InfinityIcon } from "lucide-react";
import { memo, useEffect, useState } from "react";

interface TransformationCounterProps {
  remainingTransforms: number;
  isUnlimited?: boolean;
}

// Create a non-memoized version first
const TransformationCounterComponent = ({ 
  remainingTransforms, 
  isUnlimited = false 
}: TransformationCounterProps) => {
  // More robust state management for subscription status
  const [confirmedActive, setConfirmedActive] = useState(false);
  const [currentCount, setCurrentCount] = useState(remainingTransforms);
  
  // Update the count when props change
  useEffect(() => {
    setCurrentCount(remainingTransforms);
  }, [remainingTransforms]);
  
  // Check localStorage on mount and when values might change
  useEffect(() => {
    const checkSubscriptionStatus = () => {
      // For new users, check if they actually have a confirmed active subscription
      const isLocalStorageActivated = localStorage.getItem('subscription_confirmed_active') === 'true';
      
      // Only consider confirmed if subscription_confirmed_active is explicitly true
      setConfirmedActive(isLocalStorageActivated);
    };
    
    // Check immediately on mount
    checkSubscriptionStatus();
    
    // Set up event listener for storage changes (in case another tab updates it)
    window.addEventListener('storage', checkSubscriptionStatus);
    
    // Also set up a custom event listener for our own app updates
    window.addEventListener('subscription_updated', checkSubscriptionStatus);
    
    return () => {
      window.removeEventListener('storage', checkSubscriptionStatus);
      window.removeEventListener('subscription_updated', checkSubscriptionStatus);
    };
  }, []);

  // Use either the passed prop or the localStorage confirmation, but prioritize the confirmed state
  // For new users, this will be false until they subscribe or use free transforms
  const displayUnlimited = isUnlimited && confirmedActive;

  return (
    <Badge 
      variant={displayUnlimited ? "default" : "secondary"} 
      className={`gap-1 ${displayUnlimited ? 'bg-primary text-primary-foreground' : ''}`}
    >
      {displayUnlimited ? (
        <>
          <InfinityIcon className="w-3 h-3" />
          Unlimited transforms
        </>
      ) : (
        <>
          <SparklesIcon className="w-3 h-3" />
          {currentCount} free transforms left
        </>
      )}
    </Badge>
  );
};

// Memoize the component to prevent unnecessary re-renders
export const TransformationCounter = memo(TransformationCounterComponent);
